import { TypedDocumentNode, gql } from '@apollo/client';
import { useCallback } from 'react';

import { useWalletContext } from 'contexts/wallet';
import { isType } from 'lib/gql';

import type { useGetAuthorizationApprovals_authorizationRequest } from './__generated__/useGetAuthorizationApprovals.graphql';

export const useGetAuthorizationApprovals = () => {
  const { approveAuthorizationRequests } = useWalletContext();

  return useCallback(
    async (
      authorizations: useGetAuthorizationApprovals_authorizationRequest[]
    ) => {
      const walletAuthorizationRequests = authorizations
        .map(({ fingerprint, request }) =>
          isType(request, 'StripeCreditCardAuthorizationRequest') ||
          isType(request, 'MangopayCreditCardAuthorizationRequest') ||
          isType(request, 'CheckoutCreditCardAuthorizationRequest') ||
          isType(request, 'PaypalAuthorizationRequest') ||
          isType(request, 'MangopayKlarnaAuthorizationRequest') ||
          isType(request, 'MangopayApplePayAuthorizationRequest') ||
          isType(request, 'StarkexV6TokenTransferAuthorizationRequest')
            ? null
            : {
                ...request,
                fingerprint,
              }
        )
        .filter(Boolean)
        .map(request => {
          if (isType(request, 'EthereumBankTransferAuthorizationRequest')) {
            return {
              ...request,
              amount: request.ethereumBankTransferAmount,
              feeAmount: request.ethereumBankTransferFeeAmount,
            };
          }
          if (isType(request, 'StarkexTransferAuthorizationRequest')) {
            return { ...request, amount: request.weiAmount };
          }
          if (isType(request, 'BaseWithdrawalAuthorizationRequest')) {
            return {
              ...request,
              amount: request.baseWithdrawalAmount,
              feeAmount: request.baseWithdrawalFeeAmount,
            };
          }
          if (isType(request, 'SolanaTokenTransferAuthorizationRequest')) {
            return {
              ...request,
              senderAddress: request.solanaSenderAddress,
              receiverAddress: request.solanaReceiverAddress,
              nonce: request.solanaTokenTransferApprovalNonce,
              expirationTimestamp:
                request.solanaTokenTransferApprovalExpirationTimestamp,
            };
          }
          return request;
        });
      return approveAuthorizationRequests(walletAuthorizationRequests);
    },
    [approveAuthorizationRequests]
  );
};

useGetAuthorizationApprovals.fragments = {
  authorizationRequest: gql`
    fragment useGetAuthorizationApprovals_authorizationRequest on AuthorizationRequest {
      id
      fingerprint
      request {
        ... on StarkexLimitOrderAuthorizationRequest {
          vaultIdSell
          vaultIdBuy
          amountSell
          amountBuy
          tokenSell
          tokenBuy
          nonce
          expirationTimestamp
          feeInfo {
            feeLimit
            sourceVaultId
            tokenId
          }
        }
        ... on StarkexTransferAuthorizationRequest {
          weiAmount: amount
          condition
          expirationTimestamp
          feeInfoUser {
            feeLimit
            sourceVaultId
            tokenId
          }
          nonce
          receiverPublicKey
          receiverVaultId
          senderVaultId
          token
        }
        ... on SolanaTokenTransferAuthorizationRequest {
          solanaSenderAddress: senderAddress
          solanaReceiverAddress: receiverAddress
          assetId
          solanaTokenTransferApprovalNonce: nonce
          solanaTokenTransferApprovalExpirationTimestamp: expirationTimestamp
          leafIndex
          merkleTreeAddress
          transferProxyProgramAddress
          originator
        }
        ... on EthereumBankTransferAuthorizationRequest {
          senderAddress
          receiverAddress
          ethereumBankTransferAmount: amount
          ethereumBankTransferFeeAmount: feeAmount
          deadline
          salt
          proxyAddress
          contractAddress
        }
        ... on BaseWithdrawalAuthorizationRequest {
          senderAddress
          receiverAddress
          destinationAddress
          baseWithdrawalAmount: amount
          baseWithdrawalFeeAmount: feeAmount
          deadline
          salt
          proxyAddress
          contractAddress
        }
        ... on MangopayWalletTransferAuthorizationRequest {
          nonce
          amount
          currency
          operationHash
          mangopayWalletId
        }
        ... on StripeCreditCardAuthorizationRequest {
          id
          clientSecret
          amount
          paymentMethod
        }
        ... on MangopayCreditCardAuthorizationRequest {
          id
        }
        ... on PaypalAuthorizationRequest {
          id
          redirectUrl
        }
        ... on MangopayKlarnaAuthorizationRequest {
          id
          redirectUrl
        }
        ... on MangopayApplePayAuthorizationRequest {
          id
        }
      }
    }
  ` as TypedDocumentNode<useGetAuthorizationApprovals_authorizationRequest>,
};
